import { defineStore, acceptHMRUpdate } from 'pinia'
import { useAuthStore } from '@/stores/AuthStore'
import { DeviceService, DeviceType } from '@/api/client'
import type { tEmployee } from '@/classes/Employee'
import { Employee } from '@/classes/Employee'
import handleRequest from '@/helpers/handleRequest'
import { useEventStore } from './EventStore'

export enum EmpSort {
  FIRST_NAME = 'first',
  LAST_NAME = 'last',
}

function sortEmployees(employees: Employee[], sortBy: EmpSort) {
  return employees.sort((a, b) => {
    if (sortBy === EmpSort.FIRST_NAME) {
      return a.first_name.localeCompare(b.first_name)
    } else {
      return a.last_name.localeCompare(b.last_name)
    }
  })
}

type TState = {
  employees: Employee[]
  currentSelectedEmployeeId?: number
  teamleaderId?: number
  sortBy: EmpSort
  onboardedEmployeeIds: number[]
  basicClockOnboardingCompleted: boolean
}

const initialState: TState = {
  employees: [],
  currentSelectedEmployeeId: undefined,
  teamleaderId: undefined,
  sortBy: EmpSort.LAST_NAME,
  onboardedEmployeeIds: [],
  basicClockOnboardingCompleted: false,
}

const useEmployeeStore = defineStore('employeeStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    getEmployees: (state) => {
      return state.employees
    },
    clockedInEmployees: (state) => {
      return state.employees.filter((employee) => employee.isClockedIn())
    },
    clockedOutEmployees: (state) => {
      return state.employees.filter((employee) => !employee.isClockedIn())
    },
    getEmployeeById: (state) => {
      return (employee_id: number) =>
        state.employees.find((employee) => employee.id === employee_id)
    },
    teamleader: (state) => {
      return state.employees.find(
        (employee) => employee.id === state.teamleaderId,
      )
    },
    clockedInTeamMembers: (state) => {
      return state.employees.filter(
        (employee) =>
          employee.isClockedIn() && employee.id !== state.teamleaderId,
      )
    },
    clockedOutTeamMembers: (state) => {
      return state.employees.filter(
        (employee) =>
          !employee.isClockedIn() && employee.id !== state.teamleaderId,
      )
    },
    teamMembers: (state) => {
      return state.employees.filter(
        (employee) => employee.id !== state.teamleaderId,
      )
    },
    hasTeamleader: (state) => {
      return state.teamleaderId !== undefined
    },
    employee: (state) => {
      if (!state.currentSelectedEmployeeId && !state.teamleaderId) {
        return undefined
      }
      let employee = state.employees.find(
        (employee) =>
          employee.id ===
          //wenn ein current selected existiert wird der gesucht,
          // ansonsten der Teamleader als Fallback
          (state.currentSelectedEmployeeId ?? state.teamleaderId),
      )
      if (
        !employee &&
        !state.currentSelectedEmployeeId &&
        state.employees.length === 1 &&
        useAuthStore().userIsAuthenticated
      ) {
        employee = state.employees[0]
      }
      if (!employee) {
        throw new Error('employee not found')
      }
      return employee
    },
    employeeHasBeenOnboarded(): boolean {
      if (this.employee) {
        return this.onboardedEmployeeIds.includes(this.employee?.id)
      }
      return false
    },
    employeeRequiresPinChange(): boolean {
      return this.employee?.pin_change_required ?? false
    },
    employeeByQrToken: (state) => {
      return (token: string) =>
        state.employees.find((employee) => employee.qr_hash === token)
    },
    admins: (state) => {
      return state.employees.filter((employee) => employee.is_admin)
    },
  },
  actions: {
    async sync() {
      const data = (await handleRequest(DeviceService.getEmployees()))?.data
      if (!data) return

      const employees: Employee[] = []
      data.forEach((employee) => {
        employees.push(Employee.fromServerObject(employee))
        if (useAuthStore().device_type === DeviceType.PERSONAL) {
          this.currentSelectedEmployeeId = employee.id
        }
      })
      this.employees = sortEmployees(employees, this.sortBy)
    },
    async refreshEmployee(employee: Employee) {
      const data = (await handleRequest(DeviceService.getEmployee(employee.id)))
        ?.data
      if (!data) return
      // thats enough for changing and persisting the store
      Object.assign(employee, data)
    },
    setTeamleader(employee: Employee | null) {
      this.teamleaderId = employee ? employee.id : undefined
      this.currentSelectedEmployeeId = employee ? employee.id : undefined
    },
    setActiveEmployee(employee: Employee) {
      this.currentSelectedEmployeeId = employee.id
    },
    getClockedInEmployees() {
      return this.employees.filter((employee) => employee.isClockedIn())
    },
    getClockedOutEmployees() {
      return this.employees.filter((employee) => !employee.isClockedIn())
    },
    getLastClockedOutEmployee() {
      const clockedOutEmployees = this.getClockedOutEmployees()
      if (clockedOutEmployees.length === 0) {
        return undefined
      }
      clockedOutEmployees.sort((a, b) => {
        const aLastEvent = useEventStore().lastEventOfEmployee(a.id)
        const bLastEvent = useEventStore().lastEventOfEmployee(b.id)
        if (!aLastEvent || !bLastEvent) {
          return 0
        }
        return (
          new Date(bLastEvent.occured_at.toString()).getTime() -
          new Date(aLastEvent.occured_at.toString()).getTime()
        )
      })
      return clockedOutEmployees[0]
    },
    flagEmployeeAsOnboarded() {
      if (!this.employee) {
        throw new Error('employee not found')
      }
      this.onboardedEmployeeIds.push(this.employee?.id)
    },
    changeSortOfEmployees(sortBy: EmpSort) {
      this.sortBy = sortBy
      this.employees = sortEmployees(this.employees, this.sortBy)
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state, (key, value) => {
        if (key === 'employees') {
          return value.map((employeeString: tEmployee) =>
            Employee.fromServerObject(employeeString),
          )
        } else {
          return value
        }
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useEmployeeStore, import.meta.hot))
}

export { useEmployeeStore }
